/**
 * Mapbox Static Images API URL builder for a bare basemap (no pin/overlay)
 * at an explicit center + zoom. Used when a consumer draws its own overlay
 * (e.g. an SVG route line + markers) on top and therefore needs a known,
 * deterministic projection — so it must NOT use Mapbox's `auto` viewport.
 *
 * Docs: https://docs.mapbox.com/api/maps/static-images/
 */
export interface StaticMapBaseOptions {
  center: { lat: number; lng: number };
  /** Zoom level (Mapbox convention — higher = closer). */
  zoom: number;
  /** Image width in CSS pixels. */
  width: number;
  /** Image height in CSS pixels. */
  height: number;
  /** Mapbox style id. Default `streets-v12`. */
  style?: string;
  /** Device pixel ratio — `2` requests a crisp `@2x` tile. Default 2. */
  retina?: 1 | 2;
}

/**
 * Returns `null` when `NEXT_PUBLIC_MAPBOX_TOKEN` is unset or center/zoom
 * aren't finite — callers should fall back to a placeholder rather than
 * rendering a broken image.
 */
export function staticMapBaseUrl(opts: StaticMapBaseOptions): string | null {
  const token = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
  if (!token) return null;
  const { center, zoom, width, height } = opts;
  if (!Number.isFinite(center.lat) || !Number.isFinite(center.lng)) return null;
  if (!Number.isFinite(zoom)) return null;

  const style = opts.style ?? "streets-v12";
  const retinaSuffix = opts.retina === 1 ? "" : "@2x";
  const roundedZoom = Math.round(zoom * 100) / 100;
  return (
    `https://api.mapbox.com/styles/v1/mapbox/${style}/static/` +
    `${center.lng},${center.lat},${roundedZoom}/${width}x${height}${retinaSuffix}` +
    `?access_token=${encodeURIComponent(token)}`
  );
}
